import {
  ContextMenu as BPContextMenu,
  ContextMenuProps as BPContextMenuProps,
  Tree as BPTree,
  TreeNodeInfo as BPTreeNodeInfo,
  Classes,
  PopoverInteractionKind,
} from "@blueprintjs/core";
import React, {
  type JSX,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { EmptyObject, SetOptional } from "type-fest";

import { typedForwardRef, typedMemo } from "../util/typedMemo";

import { HexPopoverProps, StyleableHexPopover } from "./HexPopover";

// --- Tree types ---

/**
 * An array of numbers representing a node's position in the tree.
 *
 * @example [2, 0]
 *
 * The third top-level node (2)'s first child (0).
 */
export type HexTreeNodePath = number[];

// unfortunately we can't omit `IconName` from the `icon` prop type since it
// causes a wave of type errors in every tree event handler that expect to be
// able to pass a node with the original typing.
export type HexTreeNode<T = EmptyObject> = BPTreeNodeInfo<T>;

export type HexTreeEventHandler<T = EmptyObject> = (
  node: HexTreeNode<T>,
  nodePath: HexTreeNodePath,
  event: React.MouseEvent<HTMLElement>,
) => void;

// --- Tree ---

export interface HexTreeProps<
  T = EmptyObject,
> extends React.HTMLAttributes<HTMLDivElement> {
  /** The nodes of the tree, including their rendered content. */
  nodes: readonly HexTreeNode<T>[];
  /** The visual size of the tree. */
  size?: HexTreeSize;
  /** Use uniform indentation for trees with arbitrary nesting. */
  indentation?: "schema" | "uniform";
  /** Additional props for the popover. Only applies if `popoverRenderContent` is provided. */
  popoverProps?: Partial<
    Omit<HexTreePopoverProps<T>, "renderContent" | "children">
  >;
  /** A render function for the popover content. */
  popoverRenderContent?: HexTreePopoverProps<T>["renderContent"];
  /** Additional props for the context menu. Only applies if `contextMenuRenderContent` is provided. */
  contextMenuProps?: Partial<
    Omit<HexTreeContextMenuProps<T>, "renderContent" | "children">
  >;
  /** A render function for the context menu content. */
  contextMenuRenderContent?: HexTreeContextMenuProps<T>["renderContent"];
  /** A callback fired when a node is clicked. */
  onNodeClick?: HexTreeEventHandler<T>;
  /** A callback fired when the caret icon of an expanded node is clicked. */
  onNodeCollapse?: HexTreeEventHandler<T>;
  /** A callback fired when a node is right-clicked. */
  onNodeContextMenu?: HexTreeEventHandler<T>;
  /** A callback fired when the caret icon of a collapsed node is clicked. */
  onNodeExpand?: HexTreeEventHandler<T>;
  /** A callback fired when the mouse is moved over a node. */
  onNodeMouseEnter?: HexTreeEventHandler<T>;
  /** A callback fired when the mouse is moved out of a node. */
  onNodeMouseLeave?: HexTreeEventHandler<T>;
}

export const HexTree = typedMemo(
  typedForwardRef(function HexTree<T = EmptyObject>(
    {
      contextMenuProps,
      contextMenuRenderContent,
      indentation,
      nodes,
      onNodeClick,
      onNodeCollapse,
      onNodeContextMenu,
      onNodeExpand,
      onNodeMouseEnter,
      onNodeMouseLeave,
      popoverProps,
      popoverRenderContent,
      size,
      ...rest
    }: HexTreeProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) {
    return (
      <MaybeHexTreePopover<T>
        {...popoverProps}
        renderContent={popoverRenderContent}
      >
        {(treePopoverProps) => (
          <MaybeHexTreeContextMenu<T>
            {...contextMenuProps}
            renderContent={contextMenuRenderContent}
          >
            {(treeContextMenuProps) => (
              <HexTreeContainer
                ref={ref}
                indentation={indentation}
                {...rest}
                size={size}
              >
                <BPTree<T>
                  contents={nodes}
                  onNodeClick={onNodeClick}
                  onNodeCollapse={onNodeCollapse}
                  // eslint-disable-next-line react/jsx-no-bind -- not memoizable
                  onNodeContextMenu={(...args) => {
                    onNodeContextMenu?.(...args);
                    treeContextMenuProps?.onNodeContextMenu?.(...args);
                  }}
                  onNodeExpand={onNodeExpand}
                  // eslint-disable-next-line react/jsx-no-bind -- not memoizable
                  onNodeMouseEnter={(...args) => {
                    onNodeMouseEnter?.(...args);
                    treePopoverProps?.onNodeMouseEnter?.(...args);
                  }}
                  // eslint-disable-next-line react/jsx-no-bind -- not memoizable
                  onNodeMouseLeave={(...args) => {
                    onNodeMouseLeave?.(...args);
                    treePopoverProps?.onNodeMouseLeave?.(...args);
                  }}
                />
              </HexTreeContainer>
            )}
          </MaybeHexTreeContextMenu>
        )}
      </MaybeHexTreePopover>
    );
  }),
);

function MaybeHexTreePopover<T = EmptyObject>({
  children,
  renderContent,
  ...rest
}: Omit<SetOptional<HexTreePopoverProps<T>, "renderContent">, "children"> & {
  children: (props: Partial<HexTreePopoverChildrenProps<T>>) => JSX.Element;
}): JSX.Element {
  if (renderContent) {
    return (
      <HexTreePopover renderContent={renderContent} {...rest}>
        {children}
      </HexTreePopover>
    );
  }
  return <>{children({})}</>;
}

function MaybeHexTreeContextMenu<T = EmptyObject>({
  children,
  renderContent,
  ...rest
}: Omit<
  SetOptional<HexTreeContextMenuProps<T>, "renderContent">,
  "children"
> & {
  children: (props: Partial<HexTreeContextMenuChildrenProps<T>>) => JSX.Element;
}): JSX.Element {
  if (renderContent) {
    return (
      <HexTreeContextMenu renderContent={renderContent} {...rest}>
        {children}
      </HexTreeContextMenu>
    );
  }
  return <>{children({})}</>;
}

// --- Tree container ---

/** `small` is 24px, `medium` is 28px, and `default` is 32px. */
export type HexTreeSize = "small" | "medium" | "default";

export interface HexTreeContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: HexTreeSize;
  indentation?: HexTreeProps["indentation"];
}

export const HexTreeContainer = React.memo(
  ({
    indentation = "schema",
    ref,
    size = "default",
    ...rest
  }: HexTreeContainerProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return (
      <Container ref={ref} {...rest} $indentation={indentation} $size={size} />
    );
  },
);

const TREE_NODE_HEIGHT_PX: Record<HexTreeSize, number> = {
  small: 24,
  medium: 28,
  default: 32,
};

const getTreeNodeHeight = ({
  $size = "default",
}: {
  $size?: HexTreeContainerProps["size"];
}): number => TREE_NODE_HEIGHT_PX[$size];

const PSEUDO_HOVER_CLASS = "pseudo-hover";

const Container = styled.div<{
  $indentation: HexTreeContainerProps["indentation"];
  $size: HexTreeContainerProps["size"];
}>`
  position: relative;
  font-size: ${({ $size, theme }) =>
    $size === "small" ? theme.fontSize.SMALL : theme.fontSize.DEFAULT};

  ${({ $indentation, $size }) =>
    $indentation === "schema" &&
    css`
      .${Classes.TREE_NODE_CONTENT}-0 {
        position: sticky;
        top: 0;
        z-index: 1;
        padding-left: 6px;
      }

      .${Classes.TREE_NODE_CONTENT}-1 {
        position: sticky;
        top: ${getTreeNodeHeight({ $size })}px;
        z-index: 1;
        padding-left: 15px;
      }

      .${Classes.TREE_NODE_CONTENT}-2 {
        width: 100%;
        padding: 0 2px 0 10px;

        .${Classes.TREE_NODE_CARET_NONE} {
          display: none;
        }
      }
    `}

  .${Classes.TREE_NODE_LABEL} {
    margin-left: 4px;
  }

  .${Classes.TREE} .${Classes.TREE_NODE_ICON} {
    margin-right: 0;
  }

  .${Classes.COLLAPSE_BODY} {
    position: relative;
    width: 100%;
  }

  ${({ $indentation }) =>
    $indentation === "schema" &&
    css`
      /* Left padding for the third level */
      .${Classes.COLLAPSE_BODY} .${Classes.COLLAPSE_BODY} {
        .${Classes.TREE_NODE_LIST}
          .${Classes.TREE_NODE}
          .${Classes.TREE_NODE_CONTENT} {
          padding-left: 54px;
        }
      }
    `}

  .${Classes.TREE_NODE} {
    position: relative;
    cursor: pointer;

    .${Classes.TREE_NODE_CONTENT} {
      height: ${getTreeNodeHeight}px;

      > * {
        z-index: 1;
      }
      .${Classes.TREE_NODE_CARET_NONE} {
        min-width: 22px;
        padding: 3px;
      }
      .${Classes.TREE_NODE_CARET} {
        min-width: 22px;
        min-height: 22px;
        padding: 3px;

        transition: transform ${({ theme }) => theme.animation.duration}
          ${({ theme }) => theme.animation.easing};
      }

      .${Classes.TREE_NODE_LABEL} {
        user-select: text;
      }
    }

    .${Classes.COLLAPSE_BODY} {
      transition: transform ${({ theme }) => theme.animation.duration}
        ${({ theme }) => theme.animation.easing};
    }
  }

  /* hover styles */
  .${Classes.TREE_NODE}:not(.${Classes.DISABLED}) {
    & > .${Classes.TREE_NODE_CONTENT} {
      &.${PSEUDO_HOVER_CLASS} {
        background-color: ${({ theme }) => theme.NonTransparentHoverColor};
      }

      &:hover {
        background-color: ${({ theme }) => theme.NonTransparentHoverColor};

        .${Classes.TREE_NODE_CARET} {
          svg path {
            stroke: ${({ theme }) => theme.NonTransparentHoverColor};
          }
        }
      }

      .${Classes.TREE_NODE_CARET} {
        &:hover {
          color: ${({ theme }) => theme.fontColor.MUTED};
        }
      }
    }
  }

  /* HACK: make the blueprint chevron look like ours since we can't supply
  a custom component. Since the icon uses fill only and not stroke, we add
  a stroke and make it the same background color as the node -- requires
  setting a default background color on the node. Unfortunately, this means
  the tree can't just have a transparent background by default, and consumers
  must override these styles. */
  .${Classes.TREE_NODE_CARET_OPEN}, .${Classes.TREE_NODE_CARET_CLOSED} {
    opacity: 0.75;
    svg path {
      stroke: ${({ theme }) => theme.backgroundColor.DEFAULT};
      stroke-width: 0.7px;
    }
  }
  .${Classes.TREE_NODE_CONTENT}-0 {
    background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
  }
  .${Classes.TREE_NODE_CONTENT}-1 {
    background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
  }

  /* Selected tree node styles */
  .${Classes.TREE_NODE}.${Classes.TREE_NODE_SELECTED} {
    > .${Classes.TREE_NODE_CONTENT} {
      z-index: 1;

      color: ${({ theme }) => theme.menuItem.activeText};
      background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};

      &::after {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${({ theme }) => theme.menuItem.activeBackground};
      }

      .${Classes.ICON} > svg {
        color: ${({ theme }) => theme.menuItem.activeText};
      }

      .${Classes.TREE_NODE_CARET} {
        svg path {
          stroke: ${({ theme }) => theme.backgroundColor.DEFAULT};
        }
      }
    }
  }
  .${Classes.TREE_NODE_CARET_NONE} {
    min-width: 18px;
  }
`;

// --- Tree context menu ---

export interface HexTreeContextMenuProps<T = EmptyObject> extends Omit<
  BPContextMenuProps,
  "content" | "children"
> {
  renderContent: (params: {
    node: HexTreeNode<T>;
    nodePath: HexTreeNodePath;
  }) => JSX.Element | undefined;
  /** A function that passes the given props to the tree component. */
  children?: (props: HexTreeContextMenuChildrenProps<T>) => React.ReactNode;
}

/** Render props for the tree of a tree context menu. */
export interface HexTreeContextMenuChildrenProps<T = EmptyObject> extends Pick<
  HexTreeProps<T>,
  "onNodeContextMenu"
> {}

export const HexTreeContextMenu = typedMemo(function HexTreeContextMenu<
  T = EmptyObject,
>({
  children: childrenProp,
  disabled,
  renderContent,
  ...rest
}: HexTreeContextMenuProps<T>) {
  const [data, setData] = useState<{
    node: HexTreeNode<T>;
    nodePath: HexTreeNodePath;
    nodeElement: HTMLElement;
  } | null>(null);

  const handlePopoverClosing = useCallback(() => {
    if (!data) return;

    data.nodeElement
      ?.closest(`.${Classes.TREE_NODE_CONTENT}`)
      ?.classList.remove(PSEUDO_HOVER_CLASS);
  }, [data]);

  const handlePopoverOpening = useCallback(() => {
    if (!data) return;
    data.nodeElement
      ?.closest(`.${Classes.TREE_NODE_CONTENT}`)
      ?.classList.add(PSEUDO_HOVER_CLASS);
  }, [data]);

  // A bit of a hack, but this serves to make sure we maintain hover state
  // on the right-clicked node when the context menu is open
  const popoverProps = useMemo<BPContextMenuProps["popoverProps"]>(() => {
    return {
      onClosing: handlePopoverClosing,
      onOpening: handlePopoverOpening,
    };
  }, [handlePopoverClosing, handlePopoverOpening]);

  const content = useMemo(
    () => (data ? renderContent(data) : undefined),
    [renderContent, data],
  );

  const handleNodeContextMenu = useCallback(
    (params: {
      node: HexTreeNode<T>;
      nodePath: HexTreeNodePath;
      event: React.MouseEvent<HTMLElement>;
      onContextMenu?: React.MouseEventHandler<HTMLElement>;
    }) => {
      const { event, node, nodePath, onContextMenu } = params;

      if (event.defaultPrevented) return;

      // we have to pull this out instead of persisting the event to state
      // since `event.currentTarget` will become `undefined` outside the
      // execution context of the event handler
      const nodeElement = event.currentTarget;
      setData({ node, nodePath, nodeElement });

      onContextMenu?.(event);

      // Suppress the browser's native context menu.
      // Workaround for https://github.com/palantir/blueprint/issues/6469
      event.preventDefault();
    },
    [],
  );

  // We're using Blueprint's ContextMenu component instead of HexContextMenu since
  // we're limited to the props that blueprint's Tree component supports. We wouldn't
  // be able to translate any of the novel props that HexContextMenu injects.
  const children = useMemo<BPContextMenuProps["children"]>(() => {
    if (!childrenProp) return null;

    // eslint-disable-next-line react/display-name -- not important
    return ({ onContextMenu, popover }) => {
      return (
        <>
          {childrenProp({
            onNodeContextMenu: (node, nodePath, event) =>
              handleNodeContextMenu({ node, nodePath, event, onContextMenu }),
          })}
          {popover}
        </>
      );
    };
  }, [childrenProp, handleNodeContextMenu]);

  return (
    <BPContextMenu
      {...rest}
      content={content}
      disabled={!renderContent || disabled}
      popoverProps={popoverProps}
    >
      {children}
    </BPContextMenu>
  );
});

// --- Tree popover ---

export interface HexTreePopoverProps<T = EmptyObject> extends Omit<
  HexPopoverProps,
  | "content"
  | "children"
  | "renderTarget"
  | "popoverClassName"
  | "interactionKind"
> {
  renderContent: (params: {
    node: HexTreeNode<T>;
    nodePath: HexTreeNodePath;
  }) => JSX.Element | undefined;
  children: (props: HexTreePopoverChildrenProps<T>) => React.ReactNode;
}

export interface HexTreePopoverChildrenProps<T = EmptyObject> extends Pick<
  HexTreeProps<T>,
  "onNodeMouseEnter" | "onNodeMouseLeave"
> {}

/** Only triggers on tree node hover (mouse enter and leave) */
export const HexTreePopover = typedMemo(function HexTreePopover<
  T = EmptyObject,
>({ children: childrenProp, renderContent, ...rest }: HexTreePopoverProps<T>) {
  const [data, setData] = useState<{
    node: HexTreeNode<T>;
    nodePath: HexTreeNodePath;
    nodeElement: HTMLElement;
  } | null>(null);
  const targetRefRef = useRef<React.RefCallback<unknown> | null>(null);

  // Always return a `JSX.Element` and never `null` or `undefined`. For some reason,
  // the **initial** mouse enter popover after content is nullish will not be anchored
  // to the node element (instead, it will be anchored to the top-left of the screen or
  // relative parent).
  const content = useMemo<JSX.Element>(() => {
    if (data) {
      return renderContent(data) ?? <EmptyContent />;
    } else {
      return <EmptyContent />;
    }
  }, [renderContent, data]);

  const handleNodeMouseEnter = useCallback(
    (params: {
      node: HexTreeNode<T>;
      nodePath: HexTreeNodePath;
      event: React.MouseEvent<HTMLElement>;
      onMouseEnter?: React.MouseEventHandler<HTMLElement>;
    }) => {
      const { event, node, nodePath, onMouseEnter } = params;
      if (event.defaultPrevented) return;
      const nodeElement = event.currentTarget;
      setData({ node, nodePath, nodeElement });
      targetRefRef.current?.(nodeElement);
      onMouseEnter?.(event);
    },
    [],
  );

  const handleNodeMouseLeave = useCallback(
    (params: {
      node: HexTreeNode<T>;
      nodePath: HexTreeNodePath;
      event: React.MouseEvent<HTMLElement>;
      onMouseLeave?: React.MouseEventHandler<HTMLElement>;
    }) => {
      const { event, node, nodePath, onMouseLeave } = params;
      if (event.defaultPrevented) return;
      const nodeElement = event.currentTarget;
      setData({ node, nodePath, nodeElement });
      targetRefRef.current?.(nodeElement);
      onMouseLeave?.(event);
    },
    [],
  );

  // use combo of open state and layout effect to add/remove pseudo-hover
  // class -- we can't use the popover's onClosing/onOpening callbacks because
  // there's only one popover instance per tree and the node/data that it's
  // positioned to can change while it's open (unlike the context menu)
  const [isOpen, setIsOpen] = useState(false);
  const handlePopoverClosing = useCallback(() => {
    setIsOpen(false);
  }, []);
  const handlePopoverOpening = useCallback(() => {
    setIsOpen(true);
  }, []);
  useLayoutEffect(() => {
    if (!data?.nodeElement || !isOpen) return;
    data.nodeElement
      .closest(`.${Classes.TREE_NODE_CONTENT}`)
      ?.classList.add(PSEUDO_HOVER_CLASS);
    return () => {
      data.nodeElement
        .closest(`.${Classes.TREE_NODE_CONTENT}`)
        ?.classList.remove(PSEUDO_HOVER_CLASS);
    };
  }, [data?.nodeElement, isOpen]);

  const renderTarget = useMemo<HexPopoverProps["renderTarget"]>(() => {
    if (!childrenProp) return undefined;

    // We're limited to the props that blueprint's Tree component supports
    // eslint-disable-next-line react/display-name
    return ({ onMouseEnter, onMouseLeave, ref: targetRef }) => {
      // this is always a function, the type is just wider and it simplifies
      // our code to narrow here
      if (typeof targetRef === "function") {
        // we need to anchor the popover to the node element through the
        // target, but we aren't actually able to pass the ref to the node
        // element on render. instead we store the ref callback to call on
        // the node event
        targetRefRef.current = targetRef;
      }
      return (
        <>
          {childrenProp({
            onNodeMouseEnter: (node, nodePath, event) =>
              handleNodeMouseEnter({ node, nodePath, event, onMouseEnter }),
            onNodeMouseLeave: (node, nodePath, event) =>
              handleNodeMouseLeave({ node, nodePath, event, onMouseLeave }),
          })}
        </>
      );
    };
  }, [childrenProp, handleNodeMouseEnter, handleNodeMouseLeave]);

  return (
    <StyleableHexPopover
      content={content}
      interactionKind={PopoverInteractionKind.HOVER}
      renderTarget={renderTarget}
      onClosing={handlePopoverClosing}
      onOpening={handlePopoverOpening}
      {...rest}
    />
  );
});

const EmptyContent = styled.div`
  display: none;
`;
